import React, { useState, useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import classnames from 'classnames';
import { createUseStyles } from 'react-jss';
import deepmerge from 'deepmerge';
import { PromiseCounterContextProvider } from '@bingads-webui-react/counter-context';
import { BusinessName, PhoneNumber, TimeZone } from '@bingads-webui-cc-react/address-tax-form';
import { AddressTaxForm, TaxFormMode } from '@bingads-webui-cc-react/tax-form';
import { CurrencySelector } from '@bingads-webui-cc-react/currency-selector';
import { Constants } from '@bingads-webui-clientcenter/entity-utils';
import { AddressUtils } from '@bingads-webui-clientcenter/address';
import { LoggerContextProvider, loggerPropTypesShape, defaultLogger } from '@bingads-webui-cc-react/instrumentation-base';
import { useDeepCompareMemo } from '@bingads-webui-react/primitive-hooks';

import { getDefaultValidationRules, ValidationContextProvider, ValidationRules } from './validation';
import { useFormControls, useTriggerValidation, useData, useLoading, useLogEntry, useI18n } from './hooks';
import { AddressFormWrapper, UserLocationWrapper, BillingGroupAlert } from './controls';
import { DataManagementPropTypes, FormControlsShape, PassiveErrorsShapes, AblInfoShapes, helpPopupIdsShape } from './shapes';
import { defaultFormControls, AddressSelectorOptions, AblFormMode, Forms, logsScenario, defaultHelpPopupIds, defaultTaxData, mtValidationInfoProps } from './constants';
import { requiredFeatures } from './required-features';
import { initialAblInfoStateGetters, areAddressesEqual, hasBillingGroup } from './utils';
import { getBaseABLStyles } from './styles';

const {
  CountryCode, SecurityAction, AivAblValidationRuleName, HKStateCode,
} = Constants;
const { enterNewAddressId, selectExistingAddressId } = AddressSelectorOptions;
const { getAddressObject } = AddressUtils;
const {
  getInitialAddressData,
  getInitialUserAddressData,
  getInitialBusinessNameData,
  getInitialCountryCode,
  getInitialUserCountryCode,
  getInitialPhoneNumberData,
  getInitialPrefillAddressId,
  getInitialTaxData,
  getInitialTimeZoneData,
  getInitialCurrencyData,
  getInitialIsUseSameLocationChecked,
} = initialAblInfoStateGetters;

const useStyles = createUseStyles(getBaseABLStyles());

const BaseAccountBusinessLocationPure = ({
  customerId,
  accountId: accountIdProp,
  accountIds,
  initialAblInfo,
  mtAblInfo: mtAblInfoProp,
  moveForward,
  setLoading,
  formControls,
  ablFormMode,
  getTriggerValidationFunction,
  dataManagement,
  validationRulesArray,
  permissions,
  notificationApi,
  onAblInfoDataChanged,
  passiveErrors,
  forceBusinessAddressOpen,
  isSignupFlow,
  helpPopupIds: helpPopupIdsProp,
  alwaysShowError: isAlwaysShowError,
  isAccountCurrencyFixed,
  excludeCustomerUnsupportedBusinessLocations,
  excludeUnsupportedBusinessLocations,
  showBillingGroupAlert,
  showRadioGroupInBusinessAddressSelector,
  bypassAccountCurrencyFilter,
  shouldSupportMoreCountriesForXandr,
  authType,
  onlyIncludeBusinessAIVAddresses,
  disableCurrencyAutoSelect,
  contentAfterCurrencySelector,
}) => {
  useLogEntry({ componentName: 'BaseAccountBusinessLocationPure' });
  useLoading({ setLoading });
  const i18n = useI18n();
  const accountId = useMemo(() => (accountIds ? accountIds[0] : accountIdProp), [accountIds, accountIdProp]);
  const isReadonly = useMemo(() => ablFormMode === AblFormMode.readonly, [ablFormMode]);
  const isEnhancedABLP1Enabled = _.get(permissions, ['dynamic', requiredFeatures.EnhancedABLP1], false);
  const isGoogleAuthIntlNewSignInPageEnabled = _.get(permissions, ['dynamic', requiredFeatures.GoogleAuthIntlNewSignInPage], false);
  const isMoveHKToCountryEnabled = _.get(permissions, ['dynamic', requiredFeatures.MoveHKToCountry], false);
  const isChinaEntity1979Enabled = _.get(permissions, ['dynamic', requiredFeatures.ChinaEntity1979], false);
  const classes = useStyles({ isReadonly });
  const initialUserAddress = useMemo(() => getInitialUserAddressData(initialAblInfo), [initialAblInfo]);
  const [prefillAddressId, setPrefillAddressId] = useState(() => getInitialPrefillAddressId(initialAblInfo));
  const [businessNameData, setBusinessNameData] = useState(() => getInitialBusinessNameData(initialAblInfo));
  const [phoneNumberData, setPhoneNumberData] = useState(() => getInitialPhoneNumberData(initialAblInfo));
  const [countryCode, setCountryCode] = useState(() => getInitialCountryCode(initialAblInfo));
  const [userCountryCode, setUserCountryCode] = useState(() => getInitialUserCountryCode(initialAblInfo));
  const [addressData, setAddressData] = useState(() => getInitialAddressData(initialAblInfo));
  const [userAddressData, setUserAddressData] = useState(() => initialUserAddress);
  const [taxData, setTaxData] = useState(() => getInitialTaxData(initialAblInfo));
  const [timeZoneData, setTimeZoneData] = useState(() => getInitialTimeZoneData(initialAblInfo));
  const [currencyData, setCurrencyData] = useState(() => getInitialCurrencyData(initialAblInfo));
  const [alwaysShowError, setAlwaysShowError] = useState(isAlwaysShowError);
  const [confirmedNoGSTIN, setConfirmedNoGSTIN] = useState(false);
  const [isUseSameLocationChecked, setIsUseSameLocationChecked] = useState(() => getInitialIsUseSameLocationChecked(initialAblInfo));
  const validationRules = useMemo(() => validationRulesArray || getDefaultValidationRules({ permissions }), [permissions, validationRulesArray]);
  const { address } = addressData;

  // Temporary workaround before backend converts existing HK state ABL to HK country ABL
  // do not force open business address forms if the pilot is enabled and the state is '91' (HK)
  // otherwise the state cannot be matched as HK is already removed in state list and will automatically show the default state option which is incorrect
  if (isMoveHKToCountryEnabled && address.StateOrProvince === HKStateCode && forceBusinessAddressOpen) {
    // eslint-disable-next-line no-param-reassign
    forceBusinessAddressOpen = false;
  }

  const formControlsInitial = useDeepCompareMemo(() => deepmerge(defaultFormControls(), formControls), [formControls]);
  const helpPopupIds = useDeepCompareMemo(() => deepmerge(defaultHelpPopupIds(i18n), helpPopupIdsProp), [helpPopupIdsProp]);

  const dataFetched = useData({
    dataManagement,
    customerId,
    accountId,
    accountIds,
    countryCode,
    userCountryCode,
    initialAblInfo,
    formControls: formControlsInitial,
    isAccountCurrencyFixed,
    excludeCustomerUnsupportedBusinessLocations,
    excludeUnsupportedBusinessLocations,
    getABLVerificationStatus: _.any(validationRulesArray, rule => rule.name === AivAblValidationRuleName),
    showBillingGroupAlert,
    isReadonly,
    isEnhancedABLP1Enabled,
    bypassAccountCurrencyFilter,
    shouldSupportMoreCountriesForXandr,
    isMoveHKToCountryEnabled,
    onlyIncludeBusinessAIVAddresses,
  });
  const {
    countryList,
    businessLocationList: rawBusinessLocationList,
    provinceList,
    userProvinceList,
    addressList,
    accountData,
    accountPermissions,
    customerPermissions,
    accountBillingGroupMap,
    accountLoading,
    provinceLoading,
    userProvinceLoading,
    addressLoading,
    customerPermissionsLoading,
    timeZones,
    timeZonesPerCountryMapping,
    timeZoneDataLoading,
    currencyList,
    currencyDataLoading,
    getCurrenciesForCountryCode,
    getStatesForCountryCode,
  } = dataFetched;

  let businessLocationList = rawBusinessLocationList;
  if (['Google', 'Facebook'].includes(authType)) {
    if (isGoogleAuthIntlNewSignInPageEnabled) {
      businessLocationList = _.filter(businessLocationList, li => !['CN', 'IN'].includes(li.Code));
    } else {
      businessLocationList = _.filter(businessLocationList, li => ['US'].includes(li.Code));
    }
  }
  if (isChinaEntity1979Enabled && _.result(accountData, 'isCC1979Account', false)) {
    businessLocationList = _.filter(businessLocationList, li => li.Code === CountryCode.CN);
  }
  // this only applies for SAP apply workflow for now
  const showEmptyBusinessAIVAddresses = onlyIncludeBusinessAIVAddresses && !addressLoading && _.isEmpty(addressList); // if only business AIV addresses should be included and there is no address returned from backend, show empty list
  if (onlyIncludeBusinessAIVAddresses && !addressLoading) {
    // get country code list from business AIV verified addresses
    const businessAIVAddressesCountrySet = new Set((addressList ?? []).map(x => x?.Country));

    // filter business location list to only include countries that have business AIV verified addresses
    businessLocationList = _.filter(businessLocationList, li => businessAIVAddressesCountrySet.has(li.Code));

    // for SAP apply workflow, no default country code, so set country code to the first country in the filtered business location list
    if (_.isUndefined(countryCode) && !_.isEmpty(businessLocationList)) {
      setCountryCode(businessLocationList[0].Code);
    }
  }

  const formControlsCombined = useFormControls({
    formControls: formControlsInitial,
    countryCode,
    userCountryCode,
    countryList,
    businessLocationList,
    accountData,
    accountLoading,
    provinceLoading,
    userProvinceLoading,
    addressLoading,
    customerPermissionsLoading,
    timeZones,
    timeZoneDataLoading,
    addressList,
    prefillAddressId,
    isReadonly,
    forceBusinessAddressOpen,
    currencyList,
    currencyDataLoading,
    setAlwaysShowError,
    isEnhancedABLP1Enabled,
    userAddress: initialUserAddress.address,
    onlyIncludeBusinessAIVAddresses,
    showEmptyBusinessAIVAddresses,
  });
  // The mtAblInfo will be calculated using accountlist in client side after taxWidgetModel logic is migrated to client side and the code below Will be cleaned up.
  const mtAblInfo = useMemo(() => mtAblInfoProp || _.result(accountData, 'toAblInfoFormat', {}), [accountData, mtAblInfoProp]);
  const mtAddress = useMemo(() => _.result(mtAblInfo.addressData, 'address'), [mtAblInfo.addressData]);
  const disableAddressDelete = useMemo(() => _.get(accountData, 'ParentCustomerId') > 0 && customerId !== _.get(accountData, 'ParentCustomerId'), [accountData, customerId]);
  const setPreferredCurrencyCode = useCallback(({ countryCodeParam }) => {
    if (formControlsInitial[Forms.CurrencySelector].visible && !_.result(accountData, 'isCreated', false)) {
      const preferredCurrencyCode = _.get(_.findWhere(businessLocationList, { Code: countryCodeParam }), 'Currency');
      if (preferredCurrencyCode) {
        setCurrencyData({ currencyCode: preferredCurrencyCode });
      }
    }
  }, [accountData, businessLocationList, formControlsInitial]);

  const initCurrency = useCallback(() => {
    if (formControlsInitial[Forms.CurrencySelector].visible && !_.isEmpty(businessLocationList) && !accountLoading) {
      const originalCurrencyCode = _.get(getInitialCurrencyData(initialAblInfo), 'currencyCode') || _.get(accountData, 'CurrencyType');
      const originalCountryCode = getInitialCountryCode(initialAblInfo);
      if (originalCurrencyCode) {
        setCurrencyData({ currencyCode: originalCurrencyCode });
      } else if (originalCountryCode) {
        setPreferredCurrencyCode({ countryCodeParam: originalCountryCode });
      }
    }
  }, [accountData, accountLoading, businessLocationList, formControlsInitial, initialAblInfo, setPreferredCurrencyCode]);

  useEffect(() => {
    initCurrency();
  }, [initCurrency]);

  const onCountryChange = useCallback(({ selectedCountryCode }) => {
    const filteredAddressList = _.filter(addressList, addr => addr.Id && addr.Country === selectedCountryCode);
    setCountryCode(selectedCountryCode);

    if (selectedCountryCode === initialAblInfo.countryCode) {
      const initialBusinessNameData = getInitialBusinessNameData(initialAblInfo);
      setAddressData(getInitialAddressData(initialAblInfo));
      setPrefillAddressId(getInitialPrefillAddressId(initialAblInfo));
      setTaxData(getInitialTaxData(initialAblInfo));
      initCurrency();
      if (_.get(initialBusinessNameData, 'businessName')) {
        setBusinessNameData(initialBusinessNameData);
      }
    } else {
      setAddressData({
        address: { Country: selectedCountryCode },
        addressWithValidation: null,
        isValid: false,
      });
      const enhancedABLPrefillAddressId = _.isEmpty(filteredAddressList) ? enterNewAddressId : selectExistingAddressId;
      setPrefillAddressId(isEnhancedABLP1Enabled && formControlsInitial[Forms.BusinessAddressSelector].visible ? enhancedABLPrefillAddressId : enterNewAddressId);
      setTaxData(null);
      if (disableCurrencyAutoSelect) {
        // Keep the current currency if it's still supported by the newly selected
        // country; only clear it (forcing the user to re-pick) when it's no longer
        // valid. Previously this cleared unconditionally, wiping a still-valid
        // selection on every country change. (LocationAndCurrency)
        const currentCurrencyCode = _.get(currencyData, 'currencyCode');
        const supportedCurrencies = getCurrenciesForCountryCode(selectedCountryCode);
        const isCurrentCurrencySupported = !!currentCurrencyCode
          && _.some(supportedCurrencies, currency => currency.Code === currentCurrencyCode);
        if (!isCurrentCurrencySupported) {
          setCurrencyData(null);
        }
      } else {
        setPreferredCurrencyCode({ countryCodeParam: selectedCountryCode });
      }
    }
  }, [addressList, formControlsInitial, initCurrency, initialAblInfo, isEnhancedABLP1Enabled, setPreferredCurrencyCode, disableCurrencyAutoSelect, currencyData, getCurrenciesForCountryCode]);

  const onUserCountryChange = useCallback(({ selectedCountryCode }) => {
    setUserCountryCode(selectedCountryCode);

    if (selectedCountryCode === initialAblInfo.userCountryCode) {
      setUserAddressData(getInitialAddressData(initialAblInfo));
    } else {
      setUserAddressData({
        address: { Country: selectedCountryCode },
        addressWithValidation: null,
        isValid: false,
      });
    }
  }, [initialAblInfo]);

  const onPhoneNumberChange = useCallback(({ phoneNumberWithCallingCode, isValid }) => {
    setPhoneNumberData({ phoneNumberWithCallingCode, isValid });
  }, []);

  const onBusinessNameChange = useCallback(({ businessName, isValid }) => {
    setBusinessNameData({ businessName, isValid });
  }, []);

  const onAddressChange = useCallback((newAddressWithValidation) => {
    const newAddress = getAddressObject(newAddressWithValidation);
    setAddressData(({ address: prevAddress }) => {
      const shouldKeepPrevValidationInfo = _.isBoolean(_.get(prevAddress, 'IsValid')) && areAddressesEqual(prevAddress, newAddress);
      return {
        address: {
          ...(shouldKeepPrevValidationInfo && _.pick(prevAddress, mtValidationInfoProps)),
          ...newAddress,
        },
        addressWithValidation: newAddressWithValidation,
        isValid: newAddressWithValidation.isValid,
      };
    });
  }, []);

  const onUserAddressChange = useCallback((newAddressWithValidation) => {
    setUserAddressData({
      address: getAddressObject(newAddressWithValidation),
      addressWithValidation: newAddressWithValidation,
      isValid: newAddressWithValidation.isValid,
    });
  }, []);

  const onTaxDataChange = useCallback(({ data, taxCertification, isValid }) => {
    setTaxData({ data, taxCertification, isValid });
  }, []);

  const onTimeZoneChange = useCallback((timeZone) => {
    setTimeZoneData({ timeZone, isValid: true });
  }, []);

  const onCurrencyChange = useCallback((data) => {
    setCurrencyData({ currencyCode: data.currencyCode, isValid: data.isValid });
  }, []);

  const handlePrefillAddressChange = useCallback(({
    addressId,
    selectedAddressData,
    selectedBusinessNameData,
  }) => {
    setPrefillAddressId(addressId);
    setAddressData(selectedAddressData);
    if (isEnhancedABLP1Enabled && (
      (prefillAddressId === selectExistingAddressId && addressId === enterNewAddressId) ||
      (prefillAddressId === enterNewAddressId && addressId === selectExistingAddressId))) {
      return;
    }
    setBusinessNameData(selectedBusinessNameData);
  }, [isEnhancedABLP1Enabled, prefillAddressId]);

  const validateForm = useCallback(() => {
    const isCountryCodeValid = formControlsCombined[Forms.CountrySelector].optional || !_.isEmpty(countryCode);
    const isBusinessNameValid = !formControlsCombined[Forms.BusinessName].visible || businessNameData.isValid !== false;
    const isPhoneNumberValid = !formControlsCombined[Forms.PhoneNumber].visible || phoneNumberData.isValid !== false;
    const isAddressValid = (!formControlsCombined[Forms.AddressForm].visible && prefillAddressId !== selectExistingAddressId) || addressData.isValid !== false;
    const isTaxDataValid = formControlsCombined[Forms.TaxForm].visible ? (!taxData || taxData.isValid !== false) : true;
    const isTimeZoneValid = !formControlsCombined[Forms.TimeZone].visible || (timeZoneData && timeZoneData.isValid !== false);
    const isCurrencyValid = !formControlsCombined[Forms.CurrencySelector].visible || (currencyData && currencyData.isValid !== false);
    const isPassiveErrorsValid = _.isEmpty(passiveErrors);
    const isUserAddressValid = isUseSameLocationChecked || (!formControlsCombined[Forms.UserLocation].visible || formControlsCombined[Forms.UserLocation].optional || userAddressData.isValid !== false);

    return !!(isCountryCodeValid && isBusinessNameValid && isAddressValid && isTaxDataValid && isPhoneNumberValid && isTimeZoneValid && isCurrencyValid && isPassiveErrorsValid && isUserAddressValid);
  }, [formControlsCombined, countryCode, businessNameData.isValid, prefillAddressId, phoneNumberData.isValid, addressData.isValid, taxData, timeZoneData, currencyData, passiveErrors, isUseSameLocationChecked, userAddressData.isValid]);

  const constructAblInfoData = useCallback(() => {
    const computedUserAddressData = (isUseSameLocationChecked ? { ...addressData, address: _.omit({ ...addressData.address }, mtValidationInfoProps) } : userAddressData);
    return ({
      addressData,
      userAddressData: formControlsCombined[Forms.UserLocation].visible ? computedUserAddressData : null,
      businessNameData,
      phoneNumberData: formControlsCombined[Forms.PhoneNumber].visible ? phoneNumberData : null,
      countryCode,
      taxData: formControlsCombined[Forms.TaxForm].visible ? taxData : null,
      timeZoneData: formControlsCombined[Forms.TimeZone].visible ? timeZoneData : null,
      currencyData: formControlsCombined[Forms.CurrencySelector].visible ? currencyData : null,
      isValid: validateForm(),
      prefillAddressId,
      confirmedNoGSTIN,
    });
  }, [isUseSameLocationChecked, addressData, userAddressData, businessNameData, formControlsCombined, phoneNumberData, countryCode, taxData, timeZoneData, currencyData, validateForm, prefillAddressId, confirmedNoGSTIN]);

  const {
    currentValidationRuleName,
    onContinueValidation,
    onCancelValidation,
  } = useTriggerValidation({
    getTriggerValidationFunction,
    moveForward,
    validationRules,
    constructAblInfoData,
    isReadonly,
    isUseSameLocationChecked,
  });

  useEffect(() => {
    onAblInfoDataChanged(constructAblInfoData());
  }, [constructAblInfoData, onAblInfoDataChanged]);

  useEffect(() => {
    if (isReadonly) {
      setAddressData(getInitialAddressData(initialAblInfo));
      setBusinessNameData(getInitialBusinessNameData(initialAblInfo));
      setPhoneNumberData(getInitialPhoneNumberData(initialAblInfo));
      setTaxData(getInitialTaxData(initialAblInfo));
    }
  }, [initialAblInfo, isReadonly]);

  return (
    <div className={classnames('account-business-location', classes.accountBusinessLocation)}>
      {
        formControlsCombined[Forms.BusinessName].visible && (
          <BusinessName
            id="formgroup-businessname"
            i18n={i18n}
            businessName={businessNameData.businessName} // This only works to set display value of component programmatically on first render
            onBusinessNameChange={onBusinessNameChange}
            alwaysShowError={alwaysShowError}
            showLineByLineFormGroup
            isReadonly={isReadonly}
            passiveError={passiveErrors.BusinessName}
            helpPopupTopic={helpPopupIds[Forms.BusinessName].popupId}
            isOptional={formControlsCombined[Forms.BusinessName].optional}
            disabled={formControlsCombined[Forms.BusinessName].disabled}
          />)
      }
      {
        formControlsCombined[Forms.PhoneNumber].visible && (
          <PhoneNumber
            id="phone-number"
            i18n={i18n}
            countries={countryList}
            phoneNumber={phoneNumberData.phoneNumberWithCallingCode}
            onPhoneNumberChange={onPhoneNumberChange}
            alwaysShowError={alwaysShowError}
            initialCountryCode={_.isEmpty(address.Country) ? CountryCode.US : address.Country}
            parseCallingCode
            showCountryCallingCode
            isReadonly={isReadonly}
            isOptional={formControlsCombined[Forms.PhoneNumber].optional}
          />)
      }
      <AddressFormWrapper
        i18n={i18n}
        formControls={formControlsCombined}
        countryCode={countryCode}
        countryList={businessLocationList}
        provinceList={provinceList}
        getStatesForCountryCode={getStatesForCountryCode}
        disableCurrencyAutoSelect={disableCurrencyAutoSelect}
        onCountryChange={onCountryChange}
        handlePrefillAddressChange={handlePrefillAddressChange}
        alwaysShowError={alwaysShowError}
        address={address}
        addressList={addressList}
        mtPrefillAddressId={getInitialPrefillAddressId(mtAblInfo)}
        prefillAddressId={prefillAddressId}
        onAddressChange={onAddressChange}
        isReadonly={isReadonly}
        passiveErrors={passiveErrors}
        helpPopupIds={helpPopupIds}
        isSignupFlow={isSignupFlow}
        isEnhancedABLP1Enabled={isEnhancedABLP1Enabled}
        disableAddressDelete={disableAddressDelete}
        showRadioGroupInBusinessAddressSelector={showRadioGroupInBusinessAddressSelector}
        onlyIncludeBusinessAIVAddresses={onlyIncludeBusinessAIVAddresses}
        showEmptyBusinessAIVAddresses={showEmptyBusinessAIVAddresses}
      />
      {
        formControlsCombined[Forms.TaxForm].visible && (
          <AddressTaxForm
            i18n={i18n}
            countries={businessLocationList}
            address={address}
            data={(taxData && taxData.data) || defaultTaxData.data}
            mtData={(mtAblInfo.taxData && mtAblInfo.taxData.data) || defaultTaxData.data}
            taxCertification={(taxData && taxData.taxCertification) || defaultTaxData.taxCertification}
            mtTaxCertification={(mtAblInfo.taxData && mtAblInfo.taxData.taxCertification) || defaultTaxData.taxCertification}
            mtAddress={mtAddress}
            onValidate={onTaxDataChange}
            alwaysShowError={alwaysShowError}
            showLineByLineFormGroup
            taxFormMode={isReadonly ? TaxFormMode.ViewMode : TaxFormMode.EditMode}
            passiveErrors={passiveErrors.TaxForm}
            permissions={permissions}
            ownerAccountId={accountId}
            showTaxCertLink={accountPermissions[SecurityAction.AccountUpdate]}
            showTaxApprovalForm={customerPermissions[SecurityAction.TaxExemptionCertStatusSet]}
            vatStatus={_.result(accountData, 'VatStatus')}
            isSignupFlow={isSignupFlow}
            accountMode={_.result(accountData, 'AccountMode')}
            disabled={formControlsCombined[Forms.TaxForm].disabled}
            isXandrOnboarding={shouldSupportMoreCountriesForXandr}
          // TODO: support optional tax when backend supports
          />)
      }
      {
        formControlsCombined[Forms.TimeZone].visible && (
          <TimeZone
            i18n={i18n}
            id="timezone"
            timeZone={timeZoneData && timeZoneData.timeZone}
            onTimeZoneChange={onTimeZoneChange}
            countryCode={countryCode}
            timeZones={timeZones}
            timeZonesPerCountryMapping={timeZonesPerCountryMapping}
            helpPopupId={helpPopupIds[Forms.TimeZone].popupId}
            helpPopupAriaLabel={helpPopupIds[Forms.TimeZone].ariaLabel}
            isReadonly={isReadonly}
          // TODO: support optional timezone
          />
        )
      }
      {
        formControlsCombined[Forms.CurrencySelector].visible && (
          <>
            <CurrencySelector
              i18n={i18n}
              id="currency"
              currencyCode={_.get(currencyData, 'currencyCode')}
              onCurrencyChange={onCurrencyChange}
              currencyList={currencyList}
              helpPopupId={helpPopupIds[Forms.CurrencySelector].popupId}
              helpPopupAriaLabel={helpPopupIds[Forms.CurrencySelector].ariaLabel}
              disabled={formControlsCombined[Forms.CurrencySelector].disabled}
              isOptional={formControlsCombined[Forms.CurrencySelector].optional}
              isReadonly={isReadonly}
              allowEmpty={disableCurrencyAutoSelect}
            />
            {contentAfterCurrencySelector}
          </>
        )
      }
      <UserLocationWrapper
        i18n={i18n}
        formControls={formControlsCombined}
        alwaysShowError={alwaysShowError}
        userCountryCode={userCountryCode}
        userCountryList={countryList}
        onUserCountryChange={onUserCountryChange}
        userAddress={(userAddressData || {}).address}
        onUserAddressChange={onUserAddressChange}
        passiveErrors={passiveErrors}
        provinceList={userProvinceList}
        getStatesForCountryCode={getStatesForCountryCode}
        disableCurrencyAutoSelect={disableCurrencyAutoSelect}
        isReadonly={isReadonly}
        isUseSameLocationChecked={isUseSameLocationChecked}
        setIsUseSameLocationChecked={setIsUseSameLocationChecked}
        helpPopupIds={helpPopupIds}
      />
      {
        hasBillingGroup({ accountBillingGroupMap }) && <BillingGroupAlert i18n={i18n} />
      }
      <ValidationContextProvider
        i18n={i18n}
        customerId={customerId}
        accountId={accountId}
        accountIds={accountIds}
        dataFetched={dataFetched}
        initialAblInfo={initialAblInfo}
        currentValidationRuleName={currentValidationRuleName}
        dataManagement={dataManagement}
        getAblInfoData={constructAblInfoData}
        mtAblInfo={mtAblInfo}
        notificationApi={notificationApi}
        onContinue={onContinueValidation}
        onCancel={onCancelValidation}
        permissions={permissions}
        setAddressData={setAddressData}
        setAlwaysShowError={setAlwaysShowError}
        setConfirmedNoGSTIN={setConfirmedNoGSTIN}
        setPrefillAddressId={setPrefillAddressId}
        validateForm={validateForm}
        formControls={formControlsCombined}
      >
        <ValidationRules validationRulesStruct={validationRules} />
      </ValidationContextProvider>
    </div>
  );
};

BaseAccountBusinessLocationPure.propTypes = {
  /**
   * The customer id in current URL context.
   */
  customerId: PropTypes.number,
  /**
   * The owner account id of the ABL info.
   */
  accountId: PropTypes.number,
  /**
   * In multiple accounts mode, accountIds are required for calculating the businessLocationList, and are also used for AIV validation.
   * It can be used in more scenarios per new requirements.
   */
  accountIds: PropTypes.arrayOf(PropTypes.number),
  /**
   * The callback function that calls back a validated abl info object, which contains success, last ruleName, triggerValidationArgs and ablInfo.
   */
  moveForward: PropTypes.func.isRequired,
  /**
   * The initial abl info, which only takes effect at the first rendering.
   */
  initialAblInfo: AblInfoShapes,
  /**
    * The mt abl info, if it has a value, mtAblInfo will use it instead of fetching from restful api.
    */
  mtAblInfo: AblInfoShapes,
  /**
   * The dataManagement object to override the default dataFetch behavior. By default, jsDataCcRest will be used. CcJsDataContextProvider is required in higher-level.
   */
  dataManagement: PropTypes.shape({
    countries: DataManagementPropTypes,
    currencies: DataManagementPropTypes,
    currencyCountriesMapping: DataManagementPropTypes,
    allowedCountriesByAccountModeMapping: DataManagementPropTypes,
    subGeography: DataManagementPropTypes,
    address: DataManagementPropTypes,
    account: DataManagementPropTypes,
    accountPermissions: DataManagementPropTypes,
    customerPermissions: DataManagementPropTypes,
    verifyBusinessAddress: DataManagementPropTypes,
    verifyTaxInformation: DataManagementPropTypes,
    timeZones: DataManagementPropTypes,
    timeZonesPerCountryMapping: DataManagementPropTypes,
    countryCurrenciesMapping: DataManagementPropTypes,
  }),
  /**
   * Specify which form to display. Will support more if needed.
   */
  formControls: FormControlsShape,
  /**
   * Form mode, it can be AblFormMode.readonly, AblFormMode.create(default) and AblFormMode.update. See the definitions in constants.js.
   */
  ablFormMode: PropTypes.number,
  /**
   * The callback function that calls back the triigerValidation function at the first rendering.
   * The called-back function should be kept outside, and should be called when needed (for example, when 'Next'/'Submit' button is clicked).
   */
  getTriggerValidationFunction: PropTypes.func,
  /**
   * The permissions object, containing a 'dynamic' property which has a list of Feature pilot status.
   * The permissions shape is like: {
   *   dynamic: {
   *     ABLColombiaTax: true,
   *     ABLHungaryTax: true,
   *     ABLPortugalTax: true,
   *     EnhancedABLP1: true,
   *     ...
   *   }
   * }
   */
  permissions: PropTypes.shape({}),
  /**
   * The setLoading function passed in. Will be called when there is any active request.
   */
  setLoading: PropTypes.func,
  /**
   * The notification Api object. Right now, only showWarning is needed, it will be called when any error happened in address/tax validation.
   */
  notificationApi: PropTypes.shape({
    showWarning: PropTypes.func,
  }),
  /**
   * The custom validationRulesArray, indicating which rules are needed when validation is triggered.
   */
  validationRulesArray: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    component: PropTypes.node.isRequired,
  })),
  /**
   * The continuous abl info callbacks on any abl info change.
   */
  onAblInfoDataChanged: PropTypes.func,
  /**
   * The passive errors that will be displayed under input. Right now, BusinessName, a few TaxForm tax types, AddressForm fields are supported.
   * Will support more if needed.
   */
  passiveErrors: PassiveErrorsShapes,
  /**
   * Force BusinessName and AddressForm open per some special requirements. For example, when there is AddressForm passive error,
   * the value should be set to true to force open AddressForm.
   */
  forceBusinessAddressOpen: PropTypes.bool,
  /**
   * Whether the ABL form is in signup workflow or not. This is used for displaying different help popup in signup workflow or not.
   */
  isSignupFlow: PropTypes.bool,
  /**
   * Whether the error messages are shown automatically after it is rendered.
   */
  alwaysShowError: PropTypes.bool,
  /**
   * Custom help popupIds for different fields.
   */
  helpPopupIds: helpPopupIdsShape,
  /**
   * Whether the account currency is fixed or not.
   */
  isAccountCurrencyFixed: PropTypes.bool,
  /**
   * When the business location is also used for customer update, exclude the unsupported countries: AN, FK, VC.
   */
  excludeCustomerUnsupportedBusinessLocations: PropTypes.bool,
  /**
   * Exclude the unsupported countries: RU, BY.
   */
  excludeUnsupportedBusinessLocations: PropTypes.bool,
  /**
   * Whether the billing group alert is shown or not.
   */
  showBillingGroupAlert: PropTypes.bool,
  /**
   * Whether to show radio group in business address selector
   */
  showRadioGroupInBusinessAddressSelector: PropTypes.bool,
  /**
   * Whether to bypass account currency filter when fetching business locations. This is used in the case where the account currency
   * is fixed and we still want to show all business locations regardless of the account currency.
   */
  bypassAccountCurrencyFilter: PropTypes.bool,
  /**
   * Whether to support more countries when fetching business locations for Xandr. This is used in the case where the account currency
   * is fixed and we still want to show all business locations regardless of the account currency and support more countries
   */
  shouldSupportMoreCountriesForXandr: PropTypes.bool,
  /**
   * Authentication type, e.g., MSA, AAD, Google, Facebook, etc.
   */
  authType: PropTypes.string,
  /**
   * Whether to include Business AIV addresses when fetching business locations.
   */
  onlyIncludeBusinessAIVAddresses: PropTypes.bool,
  /**
   * Whether to disable currency auto select.
   */
  disableCurrencyAutoSelect: PropTypes.bool,
  /**
   * Content rendered immediately after the currency selector.
   */
  contentAfterCurrencySelector: PropTypes.node,
};

BaseAccountBusinessLocationPure.defaultProps = {
  customerId: null,
  accountId: null,
  accountIds: null,
  initialAblInfo: {},
  mtAblInfo: null,
  dataManagement: {
    countries: null,
    currencies: null,
    currencyCountriesMapping: null,
    subGeography: null,
    address: null,
    account: null,
    accountPermissions: null,
    customerPermissions: null,
    accountBillingGroupMap: null,
  },
  formControls: defaultFormControls(),
  ablFormMode: AblFormMode.create,
  getTriggerValidationFunction: _.noop,
  permissions: {},
  setLoading: _.noop,
  validationRulesArray: null,
  notificationApi: {
    showWarning: null,
  },
  onAblInfoDataChanged: _.noop,
  passiveErrors: {},
  forceBusinessAddressOpen: false,
  isSignupFlow: false,
  helpPopupIds: {},
  alwaysShowError: false,
  isAccountCurrencyFixed: false,
  excludeCustomerUnsupportedBusinessLocations: false,
  excludeUnsupportedBusinessLocations: true,
  showBillingGroupAlert: false,
  showRadioGroupInBusinessAddressSelector: false,
  bypassAccountCurrencyFilter: false,
  shouldSupportMoreCountriesForXandr: false,
  authType: null,
  onlyIncludeBusinessAIVAddresses: false,
  disableCurrencyAutoSelect: false,
  contentAfterCurrencySelector: null,
};

export const BaseAccountBusinessLocation = props => (
  <PromiseCounterContextProvider>
    <LoggerContextProvider logger={props.logger} defaultScenarioName={logsScenario.Default}>
      <BaseAccountBusinessLocationPure {...props} />
    </LoggerContextProvider>
  </PromiseCounterContextProvider>
);

BaseAccountBusinessLocation.propTypes = {
  /**
   * A common logger object containing a few logging functions, the interface is based on the one in @bingads-webui-cc-react/instrumentation-base
   * - trace: in use
   * - error: not in use yet
   * - startScenario: not in use yet
   * - endScenario: not in use yet
   * - logPageLoadAndNavigationDuration: not in use yet
   */
  logger: loggerPropTypesShape,
};

BaseAccountBusinessLocation.defaultProps = {
  logger: defaultLogger,
};
